<template>
  <div class="pedidos-wrapper">
    
    <div class="filters-bar">
      <button
        v-for="filtro in filtros"
        :key="filtro.key"
        :class="['filter-btn', { active: filtroAtual === filtro.key }]"
        @click="alternarFiltro(filtro.key)"
      >
        {{ filtro.label }}
        <span class="count-badge">{{ contarPedidos(filtro.key) }}</span>
        <span v-if="filtroAtual === filtro.key" class="material-icons-outlined sort-icon" :title="sortDirection === 'desc' ? 'Mais novos primeiro' : 'Mais antigos primeiro'">
          {{ sortDirection === 'desc' ? 'arrow_downward' : 'arrow_upward' }}
        </span>
      </button>
    </div>
    
    <div class="table-container">
      <table v-if="pedidosFiltrados.length > 0">
        <thead>
        <tr>
          <th>ID</th>
          <th>Cliente</th>
          <th>Qtd.</th>
          <th>Produto</th>
          <th>Total</th>
          <th>Cidade</th>
          <th>Status</th>
          <th class="actions-cell">Ações</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="pedido in pedidosFiltrados" :key="pedido.id">
          <td>#{{ pedido.id }}</td>
          <td><strong>{{ pedido.nome | shortenName }}</strong></td>
          <td>{{ pedido.quantidade }}</td>
          <td>
            <span class="sub-text">{{ pedido.produto ? pedido.produto.nome : 'Produto não encontrado' }}</span>
          </td>
          <td class="price-cell">R$ {{ pedido.valor_total ? pedido.valor_total.toFixed(2) : '0.00' }}</td>
          <td>{{ pedido.cidade || 'N/A' }}</td>
          <td>
            <span :class="['status-badge', pedido.status]">
              {{ pedido.status | statusFilter }}
            </span>
          </td>
          <td class="actions-cell">
            <button @click="openDetalhes(pedido)" class="action-btn details" title="Ver detalhes">
              <span class="material-symbols-outlined">info</span>
            </button>
            
            <button
              @click="openShare(pedido)"
              class="action-btn share"
              :class="{ 'pseudo-disabled': !canShare(pedido) }"
              title="Opções de Compartilhamento">
              <span class="material-icons-outlined">share</span>
            </button>
            
            <button
              @click="handleDeliverClick(pedido)"
              class="action-btn deliver"
              :class="{ 'pseudo-disabled': !canDeliver(pedido) }"
              title="Entregar">
              <span class="material-icons-outlined">check_box</span>
            </button>
            
            <button
              @click="handleCancelClick(pedido)"
              class="action-btn cancel"
              :class="{ 'pseudo-disabled': !canCancel(pedido) }"
              title="Cancelar/Estornar Pedido">
              <span class="material-icons-outlined">block</span>
            </button>
          </td>
        </tr>
        </tbody>
      </table>
      <div v-else class="empty-state">Nenhum pedido encontrado neste filtro.</div>
    </div>
    
    <ModalConfirmarEntrega
      v-if="pedidoParaConfirmar"
      :pedido="pedidoParaConfirmar"
      @close="pedidoParaConfirmar = null"
      @refresh="$emit('refresh')"
    />
    
    <ModalDetalhesPedido
      v-if="pedidoParaDetalhes"
      :pedido="pedidoParaDetalhes"
      @close="pedidoParaDetalhes = null"
    />
    
    <ModalCompartilhar
      v-if="pedidoParaCompartilhar"
      :pedido="pedidoParaCompartilhar"
      @close="pedidoParaCompartilhar = null"
    />
    
    <ModalCancelarPedido
      v-if="pedidoParaCancelar"
      :pedido="pedidoParaCancelar"
      @close="pedidoParaCancelar = null"
      @refresh="$emit('refresh')"
    />
  
  </div>
</template>

<script>
import { AlertBus } from '@/plugins/AlertBus.js';
import ModalConfirmarEntrega from './modals/ModalConfirmarEntrega.vue';
import ModalDetalhesPedido from './modals/ModalDetalhesPedido.vue';
import ModalCompartilhar from './modals/ModalCompartilhar.vue';
import ModalCancelarPedido from './modals/ModalCancelarPedido.vue';

export default {
  name: "AdminPedidos",
  components: {
    ModalConfirmarEntrega,
    ModalDetalhesPedido,
    ModalCompartilhar,
    ModalCancelarPedido
  },
  props: {
    pedidos: { type: Array, default: () => [] }
  },
  data() {
    return {
      // Estados dos Modais
      pedidoParaConfirmar: null,
      pedidoParaDetalhes: null,
      pedidoParaCompartilhar: null,
      pedidoParaCancelar: null,
      
      // Filtros e Ordenação
      filtroAtual: 'todos',
      sortDirection: 'desc',
      filtros: [
        { key: 'todos', label: 'Todos' },
        { key: 'entregar', label: 'Para Entregar' },
        { key: 'pendentes', label: 'Pendentes' },
        { key: 'entregues', label: 'Entregues' },
        { key: 'expirados', label: 'Expirados' },
        { key: 'cancelados', label: 'Cancelados' },
      ]
    };
  },
  created() {
    const { pedidos_filtro, pedidos_ordem } = this.$route.query;
    if (pedidos_filtro) {
      const filtroExiste = this.filtros.some(f => f.key === pedidos_filtro);
      if (filtroExiste) {
        this.filtroAtual = pedidos_filtro;
      }
    }
    if (pedidos_ordem && ['asc', 'desc'].includes(pedidos_ordem)) {
      this.sortDirection = pedidos_ordem;
    }
  },
  watch: {
    filtroAtual() { this.syncUrl(); },
    sortDirection() { this.syncUrl(); }
  },
  computed: {
    pedidosFiltrados() {
      // 1. Filtragem
      let lista = [];
      if (this.filtroAtual === 'todos') {
        lista = this.pedidos;
      } else {
        lista = this.pedidos.filter(p => this.checkFiltro(p, this.filtroAtual));
      }
      
      // 2. Ordenação
      return [...lista].sort((a, b) => {
        const idA = a.id;
        const idB = b.id;
        if (this.sortDirection === 'desc') {
          return idB - idA;
        } else {
          return idA - idB;
        }
      });
    }
  },
  methods: {
    syncUrl() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          pedidos_filtro: this.filtroAtual,
          pedidos_ordem: this.sortDirection
        }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err);
        }
      });
    },
    
    alternarFiltro(key) {
      if (this.filtroAtual === key) {
        this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
      } else {
        this.filtroAtual = key;
        this.sortDirection = 'desc';
      }
    },
    
    checkFiltro(pedido, filtro) {
      const status = pedido.status;
      switch (filtro) {
        case 'entregar': return status === 'approved';
        case 'entregues': return status === 'delivered';
        case 'pendentes': return ['created', 'pending', 'in_process'].includes(status);
        case 'expirados': return status === 'expired';
        case 'cancelados': return ['cancelled', 'rejected', 'failure', 'refunded', 'out_of_stock'].includes(status);
        default: return true;
      }
    },
    
    contarPedidos(filtro) {
      if (filtro === 'todos') return this.pedidos.length;
      return this.pedidos.filter(p => this.checkFiltro(p, filtro)).length;
    },
    
    // --- Abertura de Modais ---
    openDetalhes(pedido) { this.pedidoParaDetalhes = pedido; },
    
    canShare(pedido) { return !!pedido.telefone; },
    openShare(pedido) {
      if (this.canShare(pedido)) {
        this.pedidoParaCompartilhar = pedido;
      } else {
        AlertBus.$emit('show-alert', { message: 'Cliente não possui telefone cadastrado.', type: 'alerta' });
      }
    },
    
    canDeliver(pedido) { return pedido.status === 'approved'; },
    handleDeliverClick(pedido) {
      if (pedido.status === 'approved') {
        this.pedidoParaConfirmar = pedido;
      } else {
        this.showStatusAlert(pedido.status, 'entregar');
      }
    },
    
    canCancel(pedido) { return pedido.status === 'approved' },
    handleCancelClick(pedido) {
      const status = pedido.status;
      if (status === 'approved') {
        this.pedidoParaCancelar = pedido;
      } else {
        this.showStatusAlert(status, 'cancelar');
      }
    },
    
    // Lógica auxiliar para alertas de ações inválidas
    showStatusAlert(status, action) {
      if (status === 'delivered') {
        AlertBus.$emit('show-alert', { message: `Pedido já foi entregue.`, type: 'info' });
      } else if (['cancelled', 'refunded', 'rejected', 'failure', 'out_of_stock'].includes(status)) {
        AlertBus.$emit('show-alert', { message: `Pedido já cancelado ou inválido.`, type: 'erro' });
      } else if (['created', 'pending', 'in_process'].includes(status)) {
        if (action === 'entregar') {
          AlertBus.$emit('show-alert', { message: `Aguarde a confirmação do pagamento.`, type: 'alerta' });
        } else if (action === 'cancelar') {
          AlertBus.$emit('show-alert', { message: `Pedidos pendentes expiram automaticamente.`, type: 'info' });
        }
      } else if (status === 'expired') {
        AlertBus.$emit('show-alert', { message: `Pedido expirado.`, type: 'erro' });
      }
    }
  },
  filters: {
    statusFilter(value) {
      const map = {
        created: 'criado', approved: 'aprovado', pending: 'pendente', in_process: 'em análise',
        cancelled: 'cancelado', rejected: 'rejeitado', out_of_stock: 'sem estoque',
        failure: 'falhou', expired: 'expirado', delivered: 'entregue', refunded: 'reembolsado'
      };
      return map[value] || value;
    },
    shortenName(value) {
      if (!value) return '';
      const parts = value.trim().split(' ');
      return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]}` : value;
    }
  }
};
</script>

<style scoped src="@/assets/admin-styles.css"></style>
<style scoped>
.filters-bar {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 15px;
  margin-bottom: 5px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.filters-bar::-webkit-scrollbar { display: none; }

.filter-btn {
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  color: var(--text-secondary);
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  display: flex; align-items: center; gap: 8px;
}
.filter-btn:hover { background-color: var(--bg-icon-hover-light); color: var(--text-main); }
.filter-btn.active { background-color: var(--color-button-buy); color: white; border-color: var(--color-button-buy); }

.count-badge {
  background-color: rgba(0, 0, 0, 0.15); padding: 1px 6px; border-radius: 10px; font-size: 0.75rem; min-width: 18px; text-align: center;
}
.sort-icon { font-size: 16px; margin: 0 2px; opacity: 0.9; }
.filter-btn.active .count-badge { background-color: rgba(255, 255, 255, 0.3); color: white; }

/* Estilos de botão desabilitado visualmente */
.action-btn.pseudo-disabled { opacity: 0.4; filter: grayscale(100%); cursor: pointer; }
</style>